"use client";

import { useControllableState } from "@erp/shared-libs/hooks";
import { ImageUploader } from "./image-uploader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { Separator } from "../ui/separator";
import { Button } from "../ui/button";
import { Pencil } from "lucide-react";
import { cn } from "@erp/shared-libs/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { differenceBy } from "lodash-es";
import { MediaFile } from "@erp/shared-libs/common";
import { DEFAULT_IMAGE_MAX_DIMENSION, DEFAULT_IMAGE_MAX_SIZE } from "@erp/frontend-api/services";

interface ImageUploaderDialogProps extends PropsWithChildren {
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  mainImageFiles?: MediaFile[];
  variantImageFiles?: MediaFile[];
  exhibitionMediaId?: string;
  onSubmit: (data: { variantImageFiles: MediaFile[]; selectedMedia?: MediaFile }) => void;
  disabled?: boolean;
  className?: string;
}

export const ImageUploaderDialog = ({
  isOpen,
  setIsOpen,
  children,
  disabled,
  className,
  ...props
}: ImageUploaderDialogProps) => {
  // =========================================================
  // Hooks
  // =========================================================
  const [isDialogOpen, setIsDialogOpen] = useControllableState({
    prop: isOpen,
    defaultProp: false,
    onChange: setIsOpen,
  });

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button
          disabled={disabled}
          variant="outlinePrimary"
          className={cn(
            `border-primary-300 text-primary-300 hover:outline-primary-300 relative flex h-max cursor-pointer
            items-center overflow-hidden rounded-lg px-0 py-0 outline transition-all hover:outline-1
            [&_svg]:size-4`,
            className
          )}
        >
          <div
            className="bg-primary shadow-background absolute bottom-0 right-0 grid size-4 place-items-center rounded-br-md
              rounded-tl-md shadow-lg outline outline-white"
          >
            <Pencil className="text-primary-foreground size-2.5!" />
          </div>
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent
        hideClose
        className="sm:max-w-150 w-11/12 overflow-hidden"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <ImageUploaderDialogContent
          onClose={() => {
            setIsDialogOpen(false);
          }}
          {...props}
        />
      </DialogContent>
    </Dialog>
  );
};

const ImageUploaderDialogContent = ({
  mainImageFiles,
  variantImageFiles: variantImageFilesProp,
  exhibitionMediaId: exhibitionMediaIdProp,
  onSubmit,
  onClose,
}: Pick<
  ImageUploaderDialogProps,
  "variantImageFiles" | "onSubmit" | "exhibitionMediaId" | "mainImageFiles"
> & {
  onClose: () => void;
}) => {
  const IMAGE_LIST_LIMIT = 100;

  // =========================================================
  // Hooks
  // =========================================================
  const [exhibitionMediaId, setExhibitionMediaId] = useState<string | undefined>(
    exhibitionMediaIdProp
  );
  const [variantImageFiles, setVariantImageFiles] = useControllableState({
    defaultProp: differenceBy(variantImageFilesProp ?? [], mainImageFiles ?? [], "mediaId"),
  });

  // =========================================================
  // Variables
  // =========================================================
  const defaultTabsValue = useMemo(() => {
    if (!mainImageFiles?.length) {
      return "variant-image";
    }

    return variantImageFiles?.findIndex((x) => x.mediaId === exhibitionMediaId) !== -1
      ? "variant-image"
      : "main-image";
  }, [exhibitionMediaId, mainImageFiles?.length, variantImageFiles]);

  // =========================================================
  // Functions
  // =========================================================
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    onSubmit({
      variantImageFiles: [...(variantImageFiles ?? [])],
      selectedMedia:
        variantImageFiles?.find((x) => x.mediaId === exhibitionMediaId) ||
        mainImageFiles?.find((x) => x.mediaId === exhibitionMediaId),
    });
    // 關閉對話框
    onClose();
  }, [onSubmit, variantImageFiles, mainImageFiles, onClose, exhibitionMediaId]);

  // =========================================================
  // Render
  // =========================================================
  return (
    <>
      <DialogHeader className="px-4 pt-4 text-left">
        <DialogTitle>圖片上傳</DialogTitle>
        <DialogDescription asChild>
          <div>
            <p className="text-muted-foreground mb-4 text-wrap text-sm">
              請上傳或選擇已上傳的商品圖片，最多可上傳 {IMAGE_LIST_LIMIT} 張， 單張圖片大小限制為
              {DEFAULT_IMAGE_MAX_SIZE / 1024 / 1024} MB ，最大邊長限制為
              {DEFAULT_IMAGE_MAX_DIMENSION}
              px，點擊縮圖選擇圖片套用至規格選項。
            </p>
            <p className="text-muted-foreground text-wrap text-sm">
              請注意如果上傳的圖片沒有套用至任何規格選項，圖片將不會被儲存。
            </p>
          </div>
        </DialogDescription>
      </DialogHeader>
      <Tabs defaultValue={defaultTabsValue}>
        {/* Tab List */}
        <TabsList className="bg-transparent p-0">
          <TabsTrigger
            asChild
            data-hidden={!mainImageFiles?.length}
            value="main-image"
            className={cn(
              `data-[state=active]:text-primary relative px-12 data-[hidden=true]:hidden
              data-[state=active]:shadow-none`
            )}
          >
            <Button type="button" variant="ghost" className="group rounded-none">
              主要圖片
              <div className="bg-primary absolute -bottom-0.5 left-0 hidden h-0.5 w-full group-data-[state=active]:block" />
            </Button>
          </TabsTrigger>
          <TabsTrigger
            asChild
            value="variant-image"
            className={cn(
              "data-[state=active]:text-primary relative px-12 data-[state=active]:shadow-none"
            )}
          >
            <Button type="button" variant="ghost" className="group rounded-none">
              規格圖片
              <div className="bg-primary absolute -bottom-0.5 left-0 hidden h-0.5 w-full group-data-[state=active]:block" />
            </Button>
          </TabsTrigger>
        </TabsList>
        <div className="bg-background overflow-hidden">
          <Separator className="mb-4" />
          <TabsContent
            data-hidden={!mainImageFiles?.length}
            className="mb-4 px-4 data-[hidden=true]:hidden"
            value="main-image"
          >
            {/* 圖片選擇器 */}
            <ImageUploader
              selectionMode
              disabelUpload
              className="bg-on-background h-75 md:h-120 row-span-12"
              exhibitionMediaId={exhibitionMediaId}
              onExhibitionMediaIdChange={setExhibitionMediaId}
              imageFiles={mainImageFiles ?? []}
              imageMaxSize={DEFAULT_IMAGE_MAX_SIZE}
              imageMaxDimension={DEFAULT_IMAGE_MAX_DIMENSION}
              imageListLimit={IMAGE_LIST_LIMIT}
              onMediaFilesChange={setVariantImageFiles}
            />
          </TabsContent>
          <TabsContent className="mb-4 px-4" value="variant-image">
            {/* 圖片選擇器 */}
            <ImageUploader
              selectionMode
              className="bg-on-background h-75 md:h-120 row-span-12"
              exhibitionMediaId={exhibitionMediaId}
              onExhibitionMediaIdChange={setExhibitionMediaId}
              imageFiles={variantImageFiles ?? []}
              imageMaxSize={DEFAULT_IMAGE_MAX_SIZE}
              imageMaxDimension={DEFAULT_IMAGE_MAX_DIMENSION}
              imageListLimit={IMAGE_LIST_LIMIT}
              onMediaFilesChange={setVariantImageFiles}
            />
          </TabsContent>
          <Separator />
        </div>
      </Tabs>
      <DialogFooter className="px-4 pb-4">
        <Button type="button" variant="outline" size="sm" className="px-8" onClick={handleClose}>
          取消
        </Button>
        <Button type="button" variant="dark" size="sm" className="px-8" onClick={handleSubmit}>
          確認送出
        </Button>
      </DialogFooter>
    </>
  );
};
